import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { emailKey, type MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { diffUserAccess } from "@ext/enterprise/components/admin/settings/users/model/buildUserChanges";
import { CancelButton } from "@ext/enterprise/components/admin/ui-kit/CancelButton";
import { CloseConfirmationDialog } from "@ext/enterprise/components/admin/ui-kit/CloseConfirmationDialog";
import { FloatingAlert } from "@ext/enterprise/components/admin/ui-kit/FloatingAlert";
import { SaveButton } from "@ext/enterprise/components/admin/ui-kit/SaveButton";
import { SheetComponent } from "@ext/enterprise/components/admin/ui-kit/SheetComponent";
import t from "@ext/localization/locale/translate";
import { Field } from "@ui-kit/Field";
import { TextInput } from "@ui-kit/Input";
import { useCallback, useMemo } from "react";
import { useAnyTargetEditorSheet } from "../../../../../enterpriseCommon/hooks/useEditorSheet";
import type {
	AccessChange,
	GesCloudMember,
	MemberTarget,
	UserAccessChange,
} from "../members/types/GesCloudUsersComponentTypes";
import { MemberAccessDialogs, MemberAccessFields } from "./MemberAccessFields";
import { type MemberRoles, useMemberAccessForm } from "./useMemberAccessForm";

interface EditMemberCardProps {
	open: boolean;
	onClose: () => void;
	user?: GesCloudMember;
	aggregate: MemberAggregate;
	canManageAdmins: boolean;
	onApply: (target: MemberTarget, changes: AccessChange[]) => Promise<void>;
}

interface UseEditMemberFormArgs {
	user?: GesCloudMember | null;
	aggregate: MemberAggregate;
	onApply: (target: MemberTarget, changes: AccessChange[]) => Promise<void>;
	onClose: () => void;
}

const useEditMemberForm = ({ user, aggregate, onApply, onClose }: UseEditMemberFormArgs) => {
	const email = user?.email ?? "";
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;

	const initialRoles = useMemo<MemberRoles>(
		() => ({
			isEditor: Boolean(aggregate.usersByValue.get(emailKey(email))?.isEditor),
			isAdmin: Boolean(user?.isAdmin),
			aiAgentAccess: Boolean(user?.aiAgentAccess),
		}),
		[user, email, aggregate.usersByValue],
	);

	const memberForm = useMemberAccessForm({ email, aggregate, initialRoles, isOwner: Boolean(user?.isOwner) });
	const { isEditor, isAdmin, aiAgentAccess } = memberForm.data;
	const { original: originalAccesses, rows: accessRows, validate: validateAccesses } = memberForm.access;

	// Backend always sets `id` on invite-type members (see GetMembersService).
	const target = useMemo<MemberTarget>(
		() => (user?.type === "invite" ? { type: "invite", id: user.id as string } : { type: "user", email }),
		[user, email],
	);

	const buildChanges = useCallback(() => {
		const changes: AccessChange[] = [];

		const roleChanged = isEditor !== initialRoles.isEditor;
		const adminChanged = isAdmin !== initialRoles.isAdmin;
		if (roleChanged || adminChanged) {
			changes.push({
				kind: "setMemberRoles",
				...(roleChanged ? { role: isEditor ? "editor" : "reader" } : {}),
				...(adminChanged ? { setAdmin: isAdmin } : {}),
			});
		}

		if (aiAgentAccess !== initialRoles.aiAgentAccess) changes.push({ kind: "setAiAgentAccess", aiAgentAccess });

		// diffUserAccess only ever emits setUserAccess/removeUserAccess, narrower than its declared shared return type.
		changes.push(...(diffUserAccess(email, originalAccesses, accessRows) as UserAccessChange[]));

		return changes;
	}, [email, isAdmin, isEditor, aiAgentAccess, initialRoles, originalAccesses, accessRows]);

	const apply = useCallback((changes: AccessChange[]) => onApply(target, changes), [onApply, target]);

	const sheet = useAnyTargetEditorSheet({
		buildChanges,
		apply,
		onClose,
		validate: validateAccesses,
		backendUrl: gesCloudUrl,
	});

	return { memberForm, sheet };
};

export const EditMemberCard = (props: EditMemberCardProps) => {
	const { open, aggregate, canManageAdmins } = props;
	const { memberForm, sheet } = useEditMemberForm(props);
	const { email } = memberForm.data;

	return (
		<>
			<SheetComponent
				cancelButton={
					<CancelButton
						onClick={(e) => {
							e.preventDefault();
							sheet.requestClose();
						}}
					/>
				}
				confirmButton={
					<SaveButton
						isSaving={sheet.saving}
						onClick={async (e) => {
							e.preventDefault();
							await sheet.submit();
						}}
					/>
				}
				isOpen={open}
				onOpenChange={(next) => !next && sheet.requestClose()}
				sheetContent={
					<div className="flex flex-col gap-6">
						<FloatingAlert message={sheet.saveError.message} show={Boolean(sheet.saveError.isShown)} />
						<MemberAccessFields
							canManageAdmins={canManageAdmins}
							emailField={
								<Field
									control={(fieldProps) => <TextInput disabled value={email} {...fieldProps} />}
									layout="vertical"
									readonly
									title={t("email")}
								/>
							}
							form={memberForm}
							showAccesses
						/>
					</div>
				}
				title={
					<>
						{t("enterprise.admin.users.editing")}{" "}
						<span className="text-muted whitespace-nowrap">{email}</span>
					</>
				}
			/>

			<MemberAccessDialogs aggregate={aggregate} form={memberForm} />

			<CloseConfirmationDialog
				isOpen={sheet.showUnsaved}
				onClose={sheet.close}
				onOpenChange={sheet.setShowUnsaved}
			/>
		</>
	);
};
